import { useState } from "react";
import {
  ALL_KINDS,
  type ClashChoice,
  firmKey,
  type ImportKind,
  type ImportMode,
  type ImportPageState,
  type ImportPreviewOrg,
  changeSource,
} from "../../utils/bpaImport";
import { staleChoicesMessage } from "../../utils/bpaImportItemControl";
import type { SavedImportChoices } from "../../utils/bpaImportSessionStore";

type FocusTokens = { content: number; orgs: number };

type UrlChoices = Pick<ImportPageState, "sourceEditionBpaId" | "kinds" | "mode">;

const INITIAL_STATE: ImportPageState = {
  sourceEditionBpaId: null,
  kinds: ALL_KINDS,
  mode: "copy",
  unticked: [],
  choices: {},
  itemChoices: {},
  clicked: {},
};

/** Everything the editor has chosen on the import page, plus which section should take focus next. */
export function useImportChoices() {
  const [state, setState] = useState<ImportPageState>(INITIAL_STATE);
  const [opened, setOpened] = useState<ImportPageState>(INITIAL_STATE);
  const [restoredFor, setRestoredFor] = useState<string | null>(null);
  const [staleDropped, setStaleDropped] = useState(0);
  const [comparingKey, setComparingKey] = useState<string | null>(null);
  const [focus, setFocus] = useState<FocusTokens>({ content: 0, orgs: 0 });

  function edit(change: (current: ImportPageState) => ImportPageState) {
    setState(change);
    setStaleDropped(0);
  }

  function hydrate(fromUrl: UrlChoices) {
    const next = { ...INITIAL_STATE, ...fromUrl };
    setState(next);
    setOpened(next);
  }

  function restore(saved: SavedImportChoices, sourceEditionBpaId: string) {
    setState(current => ({ ...current, ...saved }));
    setRestoredFor(sourceEditionBpaId);
  }

  function markRestored(sourceEditionBpaId: string) {
    setRestoredFor(sourceEditionBpaId);
  }

  function dropStale(next: ImportPageState, dropped: number) {
    setState(next);
    setStaleDropped(dropped);
  }

  function replaceState(next: ImportPageState) {
    edit(() => next);
  }

  function chooseSource(editionBpaId: string) {
    edit(current => ({ ...changeSource(current, editionBpaId), itemChoices: {}, clicked: {} }));
    setComparingKey(null);
    setFocus(current => ({ ...current, content: current.content + 1 }));
  }

  function clearSource() {
    edit(current => ({
      ...current,
      sourceEditionBpaId: null,
      unticked: [],
      choices: {},
      itemChoices: {},
      clicked: {},
    }));
    setComparingKey(null);
  }

  function toggleKind(kind: ImportKind) {
    const wasEmpty = state.kinds.length === 0;
    const kinds = state.kinds.includes(kind)
      ? state.kinds.filter(existing => existing !== kind)
      : ALL_KINDS.filter(existing => existing === kind || state.kinds.includes(existing));
    edit(current => ({ ...current, kinds }));
    if (wasEmpty) setFocus(current => ({ ...current, orgs: current.orgs + 1 }));
  }

  function setMode(mode: ImportMode) {
    setState(current => ({ ...current, mode }));
  }

  function toggleOrg(key: string) {
    edit(current => ({
      ...current,
      unticked: current.unticked.includes(key)
        ? current.unticked.filter(existing => existing !== key)
        : [...current.unticked, key],
    }));
  }

  function toggleAll(orgs: ImportPreviewOrg[], include: boolean) {
    edit(current => ({ ...current, unticked: include ? [] : orgs.map(firmKey) }));
  }

  function choose(key: string, choice: ClashChoice) {
    edit(current => ({ ...current, choices: { ...current.choices, [key]: choice } }));
  }

  return {
    state,
    opened,
    restoredFor,
    staleMessage: staleChoicesMessage(staleDropped),
    comparingKey,
    focus,
    setComparingKey,
    hydrate,
    restore,
    markRestored,
    dropStale,
    replaceState,
    update: edit,
    chooseSource,
    clearSource,
    toggleKind,
    setMode,
    toggleOrg,
    toggleAll,
    choose,
  };
}
